import { useAtom } from 'jotai';

import {
  type ClassroomDisciplineListItemViewModel,
  useGetApiV1Classroomdiscipline,
} from '@/shared/api/generated';

import { classPlanningFiltersAtom } from '../store/atoms/classPlanningFiltersAtom';
import type { ClassPlanningSortField } from '../types/classPlanningFilters';

/** Itens por página enviados à API (`PageSize`). */
export const CLASS_PLANNING_PAGE_SIZE = 20;

/** Mapeia a coluna ordenável (id da UI) para o nome de campo esperado pelo `SortField` da API. */
const SORT_FIELD_PARAM: Record<ClassPlanningSortField, string> = {
  dateStart: 'DateStart',
  disciplineName: 'DisciplineName',
  status: 'Status',
};

/**
 * Envelope paginado que o backend pode retornar nas listagens
 * (`{ data, currentPage, totalPages, totalCount }`). O spec OpenAPI declara a
 * resposta como um array puro, mas o mesmo controller (`classPlanningController`)
 * já demonstrou em `useClassModuleList` que o runtime pode entregar este envelope.
 * O hook tolera ambos os formatos.
 */
interface ClassPlanningEnvelope {
  readonly data?: ClassroomDisciplineListItemViewModel[] | null;
  readonly currentPage?: number;
  readonly totalPages?: number;
  readonly totalCount?: number;
}

/**
 * Hook para listar planejamentos de aula com filtros e paginação server-side.
 *
 * Lê o átomo de filtros, monta os query params do endpoint
 * `GET /api/v1/classroomdiscipline` (com spreads condicionais) e envolve o hook
 * gerado pelo Kubb. Aceita tanto um array puro quanto o envelope paginado do
 * backend — quando o envelope traz `totalPages`/`totalCount`, essa metadata é
 * usada; caso contrário a próxima página é inferida por página cheia.
 *
 * @returns Dados da página atual e estado de loading/error/paginação.
 *
 * @example
 * ```tsx
 * const { data, isLoading, currentPage, hasNextPage, totalCount } = useClassPlanningList();
 * ```
 */
export function useClassPlanningList() {
  const [filters] = useAtom(classPlanningFiltersAtom);

  const query = useGetApiV1Classroomdiscipline({
    PageNumber: filters.page,
    PageSize: CLASS_PLANNING_PAGE_SIZE,
    ...(filters.companyId !== null ? { CompanyId: filters.companyId } : {}),
    ...(filters.courseId !== null ? { CourseId: filters.courseId } : {}),
    ...(filters.classStudentId !== null ? { ClassStudentId: filters.classStudentId } : {}),
    ...(filters.classModuleId !== null ? { ClassModuleId: filters.classModuleId } : {}),
    ...(filters.status.length > 0 ? { Status: [...filters.status] } : {}),
    ...(filters.dateStart ? { DateStart: filters.dateStart } : {}),
    ...(filters.dateEnd ? { DateEnd: filters.dateEnd } : {}),
    ...(filters.teacherName ? { Teacher: filters.teacherName } : {}),
    ...(filters.sortField
      ? { SortField: SORT_FIELD_PARAM[filters.sortField], SortDirection: filters.sortDirection }
      : {}),
  });

  const raw = query.data as
    | ClassroomDisciplineListItemViewModel[]
    | ClassPlanningEnvelope
    | undefined;

  const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
  const envelope = Array.isArray(raw) ? undefined : raw;

  const currentPage = envelope?.currentPage ?? filters.page;
  const totalPages = envelope?.totalPages;
  const totalCount = envelope?.totalCount;

  const hasNextPage =
    totalPages !== undefined ? currentPage < totalPages : data.length === CLASS_PLANNING_PAGE_SIZE;

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    currentPage,
    pageSize: CLASS_PLANNING_PAGE_SIZE,
    hasNextPage,
    totalPages,
    totalCount,
  } as const;
}
